import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  BATCH_ATTENDANCE_QUERY_KEYS,
  getBatchMonthlyView,
  type MonthlyAttendanceView,
} from './api';
import { mergeMonthViews, monthsInRange } from './attendance-range';
import type { MatrixGroup } from './AttendanceMatrix';

export interface RangeBatch {
  id: string;
  name: string;
}

export interface AttendanceRangeResult {
  groups: MatrixGroup[];
  /** True until every month payload behind the range has arrived once. */
  isPending: boolean;
  isFetching: boolean;
  error: Error | null;
}

/**
 * Attendance for a set of batches over an arbitrary date range.
 *
 * There is no range RPC — this composes the existing per-month view instead,
 * one query per (batch, month) the range touches. Two consequences worth
 * keeping in mind:
 *
 *  - A week that straddles a month boundary costs two queries per batch, and a
 *    week that doesn't costs one. Nothing else changes.
 *  - The query keys are the same ones the full register uses, so opening the
 *    register after browsing the dashboard is a cache hit, and a mark made in
 *    either place invalidates the other.
 */
export function useAttendanceRange(
  batches: readonly RangeBatch[],
  from: string,
  to: string,
): AttendanceRangeResult {
  const months = useMemo(() => monthsInRange(from, to), [from, to]);
  const combos = useMemo(
    () => batches.flatMap((batch) => months.map((m) => ({ batch, ...m }))),
    [batches, months],
  );
  // `combine` gets results positionally, so the batch each one belongs to has
  // to be recoverable from the same order the queries were declared in.
  const batchIdByIndex = useMemo(() => combos.map((c) => c.batch.id), [combos]);

  return useQueries({
    queries: combos.map((c) => ({
      queryKey: BATCH_ATTENDANCE_QUERY_KEYS.month(c.batch.id, c.year, c.month),
      queryFn: () => getBatchMonthlyView(c.batch.id, c.year, c.month),
    })),
    // `combine` keeps the derived groups referentially stable across renders
    // that didn't change any payload — the matrix memoizes on `groups`.
    combine: (results): AttendanceRangeResult => {
      const perBatch = new Map<string, MonthlyAttendanceView[]>();
      const ids = batchIdByIndex.values();
      let isPending = false;
      let isFetching = false;
      let error: Error | null = null;

      for (const r of results) {
        const batchId = ids.next().value;
        if (batchId === undefined) break;
        if (r.isPending) isPending = true;
        if (r.isFetching) isFetching = true;
        if (r.error !== null && error === null) error = r.error;
        if (r.data === undefined) continue;
        const bucket = perBatch.get(batchId);
        if (bucket === undefined) perBatch.set(batchId, [r.data]);
        else bucket.push(r.data);
      }

      const groups: MatrixGroup[] = [];
      for (const batch of batches) {
        const views = perBatch.get(batch.id);
        if (views === undefined) continue;
        const merged = mergeMonthViews(views);
        groups.push({
          batchId: batch.id,
          batchName: batch.name,
          days: merged.days,
          students: merged.students,
          marks: merged.marks,
        });
      }

      return { groups, isPending, isFetching, error };
    },
  });
}
